import axios from "axios";
import { getActiveEventSlug } from "@/composables/useActiveEvent";

const client = axios.create({
  baseURL: "/api",
  timeout: 10000,
});

client.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("jwt");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  // Runtime activity binding: the active event slug comes from the runtime
  // active-event (localStorage), set by the scanned store, and tags every
  // /api call with ?event=<slug> so the shared backend scopes to that
  // activity. Unset → no param → backend falls back to the featured event.
  // Never override an explicit event already on the request (e.g. 大屏綁場).
  const ev = getActiveEventSlug();
  if (ev && !(cfg.params && "event" in cfg.params)) {
    cfg.params = { ...(cfg.params || {}), event: ev };
  }
  return cfg;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("jwt");
      // Don't redirect if the 401 came from the auth endpoints themselves —
      // that would create an infinite loop on `/`. The Home page will surface
      // the auth error to the user via toast.
      const url = err.config?.url ?? "";
      const isAuthCall = url.startsWith("/auth/") || url.includes("/api/auth/");
      if (!isAuthCall && window.location.pathname !== "/") {
        // Stash the gated path so the post-relogin flow (router guard +
        // consumePendingDeepLink) sends the visitor back here instead of
        // dumping them on /me. Without this, a stale JWT on /checkin/:id
        // leaves the user stranded on home after re-auth.
        const here = window.location.pathname + window.location.search;
        if (here.startsWith("/checkin/")) {
          sessionStorage.setItem("pendingDeepLink", here);
        }
        // Soft-navigate via Vue Router instead of `window.location.href = "/"`.
        // The hard reload was cancelling in-flight asset requests (/brand/*.png)
        // and tearing down the LIFF SDK bridge mid-init — the bar-QR-after-
        // member-delete scenario surfaced both as "broken logo + 無法載入
        // LINE 登入" toasts. router.push keeps the page mount intact so LIFF
        // re-initializes against the same WebView session and image loads
        // settle naturally on the home page.
        //
        // Dynamic import avoids the client → auth-store → router circular
        // dependency at module-load time.
        import("@/router").then(({ default: router }) => {
          router.push("/");
        });
      }
    }
    return Promise.reject(err);
  }
);

export default client;
